/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { AutoAwesome } from "@mui/icons-material";
import React, { Dispatch, SetStateAction } from "react";
import { ActionType } from "../../types/SummarizeAndInformMediaObject";
import { Editor } from "@tiptap/react";
import { apiGet, apiPost } from "../../services/Api/apiCall";
import { getEditorHtml } from "../../services/Editor/editorFunctions";
import { useCase } from "../../provider/CaseProvider";
import { queryKeys } from "../../services/ReactQuery/reactQueryService";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Button, ButtonProps } from "@mui/material";

type SummarizeAndInformMediaObjectSubmitButtonProps = {
  currentActionType: ActionType | null;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsSelectDisabled: Dispatch<SetStateAction<boolean>>;
  setCurrentActionType: Dispatch<SetStateAction<ActionType | null>>;
  setSummarizeAndInformOpen: Dispatch<SetStateAction<boolean>>;
  editor: Editor | null;
  mediaObjectId: number;
  documentIdentifier?: string;
};

export default function SummarizeAndInformMediaObjectSubmitButton({
  currentActionType,
  isLoading,
  setIsLoading,
  setIsSelectDisabled,
  setCurrentActionType,
  setSummarizeAndInformOpen,
  editor,
  mediaObjectId,
  documentIdentifier,
}: SummarizeAndInformMediaObjectSubmitButtonProps) {
  const { product } = useCase();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = (callback: () => Promise<void>) => async () => {
    try {
      setIsLoading(true);
      await callback();
    } finally {
      setIsLoading(false);
    }
  };

  const SUMMARIZE_AND_INFORM_API_URL = "/lb/services/summarize_and_inform";

  const createMediaObject = () => {
    return apiPost<{ mediaObjectId: string }>(`/files/generate/${product?.productClassName}/${product?.id}`, {
      summary: getEditorHtml(editor),
      mediaObjectId: mediaObjectId,
      documentIdentifier,
    });
  };

  const closeModalAndCreateSnackbar = async () => {
    setSummarizeAndInformOpen(false);
    setCurrentActionType(null);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collections("/conversations")
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collections("media_objects")
    });

    let snackbarOptions: {
      message: string;
      link: string;
      linkText: string;
    } | null = null;

    switch (currentActionType) {
      case ActionType.ORIGINAL:
        snackbarOptions = {
          message: "Dokument erfolgreich versendet",
          link: "#messages",
          linkText: "Zur Nachricht",
        };
        break;
      case ActionType.FINISH_EXTERNAL_SUMMARY:
        snackbarOptions = {
          message: "Zusammenfassung & Dokument versendet",
          link: "#messages",
          linkText: "Zur Nachricht",
        };
        break;
      case ActionType.FINISH_INTERNAL_SUMMARY:
        snackbarOptions = {
          message: "Interne Zusammenfassung erstellt",
          link: "#documents",
          linkText: "Zum Dokument",
        };
        break;
    }

    if (snackbarOptions) {
      enqueueSnackbar(snackbarOptions.message, {
        variant: "custom",
        buttonType: "link",
        buttonLink: snackbarOptions.link,
        buttonText: snackbarOptions.linkText,
        buttonTarget: "_self",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        autoHideDuration: 5000,
      });
    }
  };

  let loadingButtonProps: ButtonProps | null = null;

  switch (currentActionType) {
    case ActionType.ORIGINAL:
      loadingButtonProps = {
        children: "Mandantschaft Informieren",
        variant: "contained",
        onClick: handleClick(async () => {
          await apiPost(`${SUMMARIZE_AND_INFORM_API_URL}/send_message/original`, { mediaObjectId });
          await closeModalAndCreateSnackbar();
        }),
      };
      break;
    case ActionType.GENERATE_INTERNAL_SUMMARY:
    case ActionType.GENERATE_EXTERNAL_SUMMARY:
      loadingButtonProps = {
        endIcon: <AutoAwesome />,
        children: "Text Erstellen",
        onClick: handleClick(async () => {
          const type = currentActionType === ActionType.GENERATE_EXTERNAL_SUMMARY ? "external" : "internal";

          let result = await apiGet<{ summary: string }>(
            `${SUMMARIZE_AND_INFORM_API_URL}/generate_summary/${type}/${mediaObjectId}`
          );

          if (result?.summary) {
            setIsSelectDisabled(true);
            editor?.commands.setContent(result?.summary);
          }

          setCurrentActionType(
            currentActionType === ActionType.GENERATE_EXTERNAL_SUMMARY
              ? ActionType.FINISH_EXTERNAL_SUMMARY
              : ActionType.FINISH_INTERNAL_SUMMARY
          );
        }),
      };
      break;
    case ActionType.FINISH_EXTERNAL_SUMMARY:
      loadingButtonProps = {
        children: "Schreiben erstellen & Mandantschaft informieren",
        variant: "contained",
        onClick: handleClick(async () => {
          let filesGenerateResponse = await createMediaObject();
          await apiPost(`${SUMMARIZE_AND_INFORM_API_URL}/send_message/external`, {
            mediaObjectId,
            summaryMediaObjectId: filesGenerateResponse?.mediaObjectId,
          });
          await closeModalAndCreateSnackbar();
        }),
      };
      break;
    case ActionType.FINISH_INTERNAL_SUMMARY:
      loadingButtonProps = {
        children: "Internes Schreiben erstellen",
        variant: "contained",
        onClick: handleClick(async () => {
          await createMediaObject();
          await closeModalAndCreateSnackbar();
        }),
      };
      break;
  }

  return loadingButtonProps ? <Button {...loadingButtonProps} loading={isLoading} /> : null;
}
